import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Animating with React, Redux, and d3",
  "description": "Particle generator. It makes tiny circles fly out of where you click. Hold down your mouse and move around. The particles keep flying out of your cursor. On mobile and only have a finger? That works, too. I’m a nerd, so this is what I consider fun. Your mileage may vary.\"",
  "date": "2016-03-11T08:00:00.000Z",
  "lastUpdated": "2016-03-11T08:00:00.000Z",
  "image": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`That’s a particle generator. It makes tiny circles fly out of where you click. Hold down your mouse and move around. The particles keep flying out of your cursor. On mobile and only have a finger? That works, too. I’m a nerd, so this is what I consider fun. Your mileage may vary. Please do click in the embed and look at those circles fly. Ain’t it cool?`}</p>
    <h2 {...{
      "id": "heres-how-it-works"
    }}>{`Here’s how it works`}</h2>
    <p>{`The whole thing is built with React, Redux, and d3. No tricks for animation; just a bit of cleverness. Here’s the general approach: We use `}<strong parentName="p">{`React to render everything`}</strong>{`: the page, the SVG element, the particles inside. All of it is built with React components that take some props and return some DOM. This lets us tap into React’s algorithms that decide which nodes to update and when to garbage collect old nodes. Then we use some `}<strong parentName="p">{`d3 calculations and event detection`}</strong>{`. D3 has great random generators, so we take advantage of that. D3’s mouse and touch event handlers calculate coordinates relative to our SVG. We need those, and React can’t do it. React’s click handlers are based on DOM nodes, which don’t correspond to `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` coordinates. D3 looks at real cursor position on screen. All `}<strong parentName="p">{`particle coordinates are in a Redux store`}</strong>{`. Each particle also has a movement vector. The store holds some useful flags and general parameters, too. This lets us treat animation as data transformations. I’ll show you what I mean in a bit. We use `}<strong parentName="p">{`actions to communicate user events`}</strong>{` like creating particles, starting the animation, changing mouse position, and so on. On each requestAnimationFrame, we `}<strong parentName="p">{`dispatch an “advance animation” action`}</strong>{`. On each action, the `}<strong parentName="p">{`reducer calculates a new state`}</strong>{` for the whole app. This includes `}<strong parentName="p">{`new particle positions`}</strong>{` for each step of the animation. When the store updates, `}<strong parentName="p">{`React flushes changes`}</strong>{` via props and because `}<strong parentName="p">{`coordinates are state, the particles move`}</strong>{`. The result is smooth animation. Keep reading to learn the details. The `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-particles-experiment"
      }}>{`code is also on GitHub`}</a>{`. A version of this article will be featured as a chapter in my upcoming `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js/"
      }}>{`React+d3js ES6 book`}</a>{`.`}</p>
    <h2 {...{
      "id": "3-presentation-components"
    }}>{`3 presentation components`}</h2>
    <p>{`We’ll start with the presentation components because they’re the least complicated. To render a collection of particles, we need:`}</p>
    <ul>
      <li parentName="ul">{`a stateless `}<inlineCode parentName="li">{`Particle`}</inlineCode></li>
      <li parentName="ul">{`a stateless `}<inlineCode parentName="li">{`Particles`}</inlineCode></li>
      <li parentName="ul">{`a proper `}<inlineCode parentName="li">{`App`}</inlineCode></li>
    </ul>
    <p>{`None of them contain state, but `}<inlineCode parentName="p">{`App`}</inlineCode>{` has to be a proper component so that we can use `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`. We need it to attach d3 event listeners. The `}<inlineCode parentName="p">{`Particle`}</inlineCode>{` component is a circle. It looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/components/Particles/Particle.jsx
\\import React, { PropTypes } from 'react';

const Particle = ({ x, y }) => (
    
);

Particle.propTypes = {
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
};

export default Particle;
`}</code></pre>
    <p>{`It takes `}<inlineCode parentName="p">{`x`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{` coordinates and returns an SVG circle. The `}<inlineCode parentName="p">{`Particles`}</inlineCode>{` component isn’t much smarter – it returns a list of circles wrapped in a grouping element, like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/components/Particles/index.jsx
\\import React, { PropTypes } from 'react';

\\import Particle from './Particle';

const Particles = ({ particles }) => (
    {particles.map(particle =>
        
        )}
    
);

Particles.propTypes = {
    particles: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired
    }).isRequired).isRequired
};

export default Particles;
`}</code></pre>
    <p>{`Note that `}<inlineCode parentName="p">{`key={particle.id}`}</inlineCode>{` part. React complains endlessly without it. I think it’s used to tell similar components apart, which makes the fancy algorithms work. Cool. Given an array of `}<inlineCode parentName="p">{`{id, x, y}`}</inlineCode>{` objects, we can render our SVG circles. Now comes our first fun component: the `}<inlineCode parentName="p">{`App`}</inlineCode>{`. `}<inlineCode parentName="p">{`App`}</inlineCode>{` takes care of rendering the scene and attaching d3 event listeners. The rendering part looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/components/index.jsx

\\import React, { PropTypes, Component } from 'react';
\\import ReactDOM from 'react-dom';
\\import d3 from 'd3';

\\import Particles from './Particles';
\\import Footer from './Footer';
\\import Header from './Header';

class App extends Component {
    render() {
        return (
            
 this.props.startTicker()} style={{overflow: 'hidden'}}>
                 

                 
                     
                 
                 

             

        );
    }
}

App.propTypes = {
    svgWidth: PropTypes.number.isRequired,
    svgHeight: PropTypes.number.isRequired,
    startTicker: PropTypes.func.isRequired,
    startParticles: PropTypes.func.isRequired,
    stopParticles: PropTypes.func.isRequired,
    updateMousePos: PropTypes.func.isRequired
};

export default App;
`}</code></pre>
    <p>{`There’s more going on, but the gist is that we return a `}<inlineCode parentName="p">{`<div>`}</inlineCode>{` with a `}<inlineCode parentName="p">{`Header`}</inlineCode>{`, a `}<inlineCode parentName="p">{`Footer`}</inlineCode>{`, and an `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{`. Inside `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{`, we use `}<inlineCode parentName="p">{`Particles`}</inlineCode>{` to render many circles. Don’t worry about the Header and Footer; they’re text. Notice that the core of our rendering function only says `}<em parentName="p">{`“Put all Particles here, please”`}</em>{`. There’s nothing about what’s moved, what’s new, or what’s no longer needed. We don’t have to worry about that. We get a list of coordinates and naively render circles. React takes care of the rest. If you ask me, that’s the real magic here. Oh, and we call `}<inlineCode parentName="p">{`startTicker()`}</inlineCode>{` when a user clicks on our scene. No reason to have the clock running `}<em parentName="p">{`before`}</em>{` any particles exist.`}</p>
    <h3 {...{
      "id": "d3-event-listeners"
    }}>{`D3 event listeners`}</h3>
    <p>{`To let users generate particles, we have to wire up those functions we mentioned in `}<inlineCode parentName="p">{`propTypes`}</inlineCode>{`. It looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/components/index.jsx

class App extends Component {
    componentDidMount() {
        let svg = d3.select(this.refs.svg);

        svg.on('mousedown', () => {
            this.updateMousePos();
            this.props.startParticles();
        });
        svg.on('touchstart', () => {
            this.updateTouchPos();
            this.props.startParticles();
        });
        svg.on('mousemove', () => {
            this.updateMousePos();
        });
        svg.on('touchmove', () => {
            this.updateTouchPos();
        });
        svg.on('mouseup', () => {
            this.props.stopParticles();
        });
        svg.on('touchend', () => {
            this.props.stopParticles();
        });
        svg.on('mouseleave', () => {
            this.props.stopParticles();
        });

    }

    updateMousePos() {
        let [x, y] = d3.mouse(this.refs.svg);
        this.props.updateMousePos(x, y);
    }

    updateTouchPos() {
        let [x, y] = d3.touches(this.refs.svg)[0];
        this.props.updateMousePos(x, y);
    }
`}</code></pre>
    <p>{`There are some events to think about:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`mousedown`}</inlineCode>{` and `}<inlineCode parentName="li">{`touchstart`}</inlineCode>{` turn on particle generation`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mousemove`}</inlineCode>{` and `}<inlineCode parentName="li">{`touchmove`}</inlineCode>{` update the mouse location`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mouseup`}</inlineCode>{`, `}<inlineCode parentName="li">{`touchend`}</inlineCode>{`, and `}<inlineCode parentName="li">{`mouseleave`}</inlineCode>{` turn off particle generation`}</li>
    </ul>
    <p>{`Then you have `}<inlineCode parentName="p">{`updateMousePos`}</inlineCode>{` and `}<inlineCode parentName="p">{`updateTouchPos`}</inlineCode>{`, which use d3’s magic to calculate new `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` coordinates relative to our SVG element. The particle generation step uses this data as each particle’s initial position. Yes, it’s complicated. Remember, React isn’t smart enough to figure out mouse position relative to our drawing area. React knows that we clicked a DOM node. `}<a parentName="p" {...{
        "href": "https://github.com/mbostock/d3/blob/master/src/event/mouse.js"
      }}>{`D3 does the magic`}</a>{` to find exact coordinates. For touch events, we only consider the first touch’s position. We `}<em parentName="p">{`could`}</em>{` let users shoot particles out of multiple fingers at once, but there’s enough going on as it is. That’s it for the rendering and the user events. `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-particles-experiment/blob/master/src/components/index.jsx"
      }}>{`107 lines of code`}</a>{`.`}</p>
    <h2 {...{
      "id": "6-actions"
    }}>{`6 Actions`}</h2>
    <p>{`Redux actions are a fancy way of saying `}<em parentName="p">{`“Yo, a thing happened!”`}</em>{`. They’re functions you call to get structured metadata about what’s up. We have 6 actions. The most complicated one looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/actions/index.jsx
export const CREATE_PARTICLES = 'CREATE_PARTICLES';

export function createParticles(N, x, y) {
    return {
        type: CREATE_PARTICLES,
        x: x,
        y: y,
        N: N
    };
}
`}</code></pre>
    <p>{`It tells the system to create `}<inlineCode parentName="p">{`N`}</inlineCode>{` particles at `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` coordinates. You’ll see how that works when we look at the Reducer, and you’ll see how it triggers when we look at the Container. Actions `}<em parentName="p">{`must`}</em>{` have a `}<inlineCode parentName="p">{`type`}</inlineCode>{`. Reducers use the type to decide what to do. `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-particles-experiment/blob/master/src/actions/index.js"
      }}>{`Our other actions`}</a>{` are `}<inlineCode parentName="p">{`tickTime`}</inlineCode>{`, `}<inlineCode parentName="p">{`tickerStarted`}</inlineCode>{`, `}<inlineCode parentName="p">{`startParticles`}</inlineCode>{`, `}<inlineCode parentName="p">{`stopParticles`}</inlineCode>{`, and `}<inlineCode parentName="p">{`updateMousePos`}</inlineCode>{`. You can guess what they mean :)`}</p>
    <h2 {...{
      "id": "1-container-component"
    }}>{`1 Container component`}</h2>
    <p>{`Containers are React components much like the presentation bits. Unlike presentation components, containers talk to the redux data store. I’m not sure this separation is strictly necessary, but it does make your code look nice. You have functionally clean presentation components that turn properties into elements, and dirty, dirty containers that talk to the outside world. You can think of them as data store monads if it helps. The gist of our `}<inlineCode parentName="p">{`AppContainer`}</inlineCode>{` looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/containers/AppContainer.jsx
\\import { connect } from 'react-redux';
\\import React, { Component } from 'react';

\\import App from '../components';
\\import { tickTime, tickerStarted, startParticles, stopParticles, updateMousePos, createParticles } from '../actions';

class AppContainer extends Component {
    componentDidMount() {
        const { store } = this.context;
        this.unsubscribe = store.subscribe(() =>
            this.forceUpdate()
        );
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

   // ...

    render() {
        const { store } = this.context;
        const state = store.getState();

        return (
            
        );
    }
};

AppContainer.contextTypes = {
    store: React.PropTypes.object
};

export default AppContainer;
`}</code></pre>
    <p>{`We `}{`\\`}{`import stuff, then we define `}<inlineCode parentName="p">{`AppContainer`}</inlineCode>{` as a proper React `}<inlineCode parentName="p">{`Component`}</inlineCode>{`. We need to use lifecycle methods, which aren’t available in stateless components. There are three `}{`\\`}{`important parts in this code:`}</p>
    <ol>
      <li parentName="ol">{`We wire up the store in `}<inlineCode parentName="li">{`componentDidMount`}</inlineCode>{` and `}<inlineCode parentName="li">{`componentWillUnmount`}</inlineCode>{`. Subscribe to data changes on mount, unsubscribe on unmount.`}</li>
      <li parentName="ol">{`When rendering, we assume the store is our context, use `}<inlineCode parentName="li">{`getState()`}</inlineCode>{`, then render the component we’re wrapping. In this case, we render the `}<inlineCode parentName="li">{`App`}</inlineCode>{` component.`}</li>
      <li parentName="ol">{`To get the store as our context, we `}<em parentName="li">{`have to`}</em>{` define `}<inlineCode parentName="li">{`contextTypes`}</inlineCode>{`. It won’t work otherwise. This is deep React magic.`}</li>
    </ol>
    <p>{`Contexts are nice because they let us implicitly pass properties. The context can be anything, but Redux prefers it be the store. That contains `}<em parentName="p">{`everything`}</em>{` about application state – both UI and business data. That’s where the monad comparison comes from … my foray into Haskell may have broken me. I see monads everywhere. In case you were wondering, that `}<inlineCode parentName="p">{`{::this.startTicker}`}</inlineCode>{` syntax comes from ES2016. It’s equivalent to `}<inlineCode parentName="p">{`{this.startTicker.bind(this)}`}</inlineCode>{`. Enable `}<inlineCode parentName="p">{`stage-0`}</inlineCode>{` in your Babel config to use it.`}</p>
    <h3 {...{
      "id": "appcontainer-talks-to-the-store"
    }}>{`AppContainer talks to the store`}</h3>
    <p>{`Great, you know the basics. Now we need to define those callbacks so `}<inlineCode parentName="p">{`App`}</inlineCode>{` can trigger actions. Most are boilerplate-y action wrappers. Like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/containers/AppContainer.jsx
    startParticles() {
        const { store } = this.context;
        store.dispatch(startParticles());
    }

    stopParticles() {
        const { store } = this.context;
        store.dispatch(stopParticles());
    }

    updateMousePos(x, y) {
        const { store } = this.context;
        store.dispatch(updateMousePos(x, y));
    }
`}</code></pre>
    <p>{`That’s boilerplate. The action function gives us that `}<inlineCode parentName="p">{`{type: ..}`}</inlineCode>{` object, and we dispatch it on the store. When that happens, Redux uses our reducer to create a new instance of the state tree. We’ll talk more about that in the next section. First, we have to look at the `}<inlineCode parentName="p">{`startTicker`}</inlineCode>{` callback. It’s where our magic begins.`}</p>
    <pre><code parentName="pre" {...{}}>{`    startTicker() {
        const { store } = this.context;

        let ticker = () => {
            if (store.getState().tickerStarted) {
                this.maybeCreateParticles();
                store.dispatch(tickTime());

                window.requestAnimationFrame(ticker);
            }
        };

        if (!store.getState().tickerStarted) {
            console.log("Starting ticker");
            store.dispatch(tickerStarted());
            ticker();
        }
    }
`}</code></pre>
    <p>{`Oof. Don’t worry if you don’t “get” this immediately. It took me a few hours to create. This drives our animation loop. Some might call it the game loop. It dispatches the `}<inlineCode parentName="p">{`tickTime`}</inlineCode>{` action on every `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{`. Every time the browser is ready to render, we get a chance to update our Redux data store. In theory, that’s 60 times a second, but it depends on many factors. Look it up. `}<inlineCode parentName="p">{`startTicker`}</inlineCode>{` updates the store in two steps:`}</p>
    <ol>
      <li parentName="ol">{`Check `}<inlineCode parentName="li">{`tickerStarted`}</inlineCode>{` flag and only start the ticker if it hasn’t been started yet. This way we don’t try to run multiple animation frames per render frame. As a result, we can be naive about binding `}<inlineCode parentName="li">{`startTicker`}</inlineCode>{` to `}<inlineCode parentName="li">{`onMouseDown`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Create a `}<inlineCode parentName="li">{`ticker`}</inlineCode>{` function that generates particles, dispatches the `}<inlineCode parentName="li">{`tickTime`}</inlineCode>{` action, and recursively calls itself on every `}<inlineCode parentName="li">{`requestAnimationFrame`}</inlineCode>{`. We check the `}<inlineCode parentName="li">{`tickerStarted`}</inlineCode>{` flag every time so we can potentially stop time.`}</li>
    </ol>
    <p>{`Yes, that means we are asynchronously dispatching redux actions. It’s okay; that sort of Just Working (tm) is one of the main benefits of immutable data. The `}<inlineCode parentName="p">{`maybeCreateParticles`}</inlineCode>{` function itself isn’t too interesting. It gets `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` coordinates from `}<inlineCode parentName="p">{`store.mousePos`}</inlineCode>{`, checks the `}<inlineCode parentName="p">{`generateParticles`}</inlineCode>{` flag, and dispatches the `}<inlineCode parentName="p">{`createParticles`}</inlineCode>{` action. That’s the container. `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-particles-experiment/blob/master/src/containers/AppContainer.jsx"
      }}>{`83 lines of code`}</a>{`.`}</p>
    <h2 {...{
      "id": "1-reducer"
    }}>{`1 Reducer`}</h2>
    <p>{`Sweet. With the actions firing and the drawing done, it’s time to look at the entire logic of our particle generator. We’ll get it done in just 33 lines of code and some change. Ok. Honestly? It’s a bunch of change. But the 33 lines that make up `}<inlineCode parentName="p">{`CREATE_PARTICLES`}</inlineCode>{` and `}<inlineCode parentName="p">{`TIME_TICK`}</inlineCode>{` changes are the most interesting. All of our logic goes in the reducer. `}<a parentName="p" {...{
        "href": "http://redux.js.org/docs/basics/Reducers.html"
      }}>{`Dan Abramov says`}</a>{` to think of reducers as the function you’d put in `}<inlineCode parentName="p">{`.reduce()`}</inlineCode>{`. Given a state and a set of changes, how do I create the new state? A simplistic example would look like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`let sum = [1,2,3,4].reduce((sum, i) => sum+i, 0);
`}</code></pre>
    <p>{`For every number, take the previous sum and add the number. Our particle generator is a more complicated version of that. It takes the current application state, incorporates an action, and returns the new application state. To keep things simple, we’ll put everything into the same reducer and use a big `}<inlineCode parentName="p">{`switch`}</inlineCode>{` statement to decide what to do based on `}<inlineCode parentName="p">{`action.type`}</inlineCode>{`. In bigger applications, we’d split this into multiple reducers, but the base principles stay the same. Let’s start with the basics:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/reducers/index.js
const Gravity = 0.5,
      randNormal = d3.random.normal(0.3, 2),
      randNormal2 = d3.random.normal(0.5, 1.8);

const initialState = {
    particles: [],
    particleIndex: 0,
    particlesPerTick: 5,
    svgWidth: 800,
    svgHeight: 600,
    tickerStarted: false,
    generateParticles: false,
    mousePos: [null, null]
};

function particlesApp(state = initialState, action) {
    switch (action.type) {
    default:
            return state;
    }
}

export default particlesApp;
`}</code></pre>
    <p>{`This is our reducer. We started with the gravity constant and two random generators. Then we defined the default state:`}</p>
    <ul>
      <li parentName="ul">{`an empty list of particles`}</li>
      <li parentName="ul">{`a particle index, which I’ll explain in a bit`}</li>
      <li parentName="ul">{`the number of particles we want to generate on each tick`}</li>
      <li parentName="ul">{`default svg sizing`}</li>
      <li parentName="ul">{`and the two flags and `}<inlineCode parentName="li">{`mousePos`}</inlineCode>{` for the generator`}</li>
    </ul>
    <p>{`Our reducer doesn’t change anything yet. It’s `}{`\\`}{`important to always return at least an unchanged state.`}</p>
    <h3 {...{
      "id": "update-the-state-to-animate"
    }}>{`Update the state to animate`}</h3>
    <p>{`For most actions, our reducer updates a single value. Like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// src/reducers/index.js
    switch (action.type) {
        case 'TICKER_STARTED':
            return Object.assign({}, state, {
                tickerStarted: true
            });
        case 'START_PARTICLES':
            return Object.assign({}, state, {
                generateParticles: true
            });
        case 'STOP_PARTICLES':
            return Object.assign({}, state, {
                generateParticles: false
            });
        case 'UPDATE_MOUSE_POS':
            return Object.assign({}, state, {
                mousePos: [action.x, action.y]
            });
`}</code></pre>
    <p>{`Even though we’re only changing values of boolean flags and two-digit arrays, `}<em parentName="p">{`we have to create a new state`}</em>{`. Always create a new state. Redux relies on application state being immutable. That’s why we use `}<inlineCode parentName="p">{`Object.assign({}, ...`}</inlineCode>{` every time. It creates a new empty object, fills it with the current state, then overwrites specific values with new ones. Either do that every time, or use a library for immutable data structures. That might work better, but I haven’t tried it yet. The two most `}{`\\`}{`important state updates – animation tick and create particles – look like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`        case 'CREATE_PARTICLES':
            let newParticles = state.particles.slice(0),
                i;

            for (i = 0; i < action.N; i++) {
                let particle = {id: state.particleIndex+i,
                                x: action.x,
                                y: action.y};

                particle.vector = [particle.id%2 ? -randNormal() : randNormal(),
                                   -randNormal2()*3.3];

                newParticles.unshift(particle);
            }

            return Object.assign({}, state, {
                particles: newParticles,
                particleIndex: state.particleIndex+i+1
            });
        case 'TIME_TICK':
            let {svgWidth, svgHeight} = state,
                movedParticles = state.particles
                                      .filter((p) =>
                                          !(p.y > svgHeight || p.x < 0 || p.x > svgWidth))
                                      .map((p) => {
                                          let [vx, vy] = p.vector;
                                          p.x += vx;
                                          p.y += vy;
                                          p.vector[1] += Gravity;
                                          return p;
                                      });

            return Object.assign({}, state, {
                particles: movedParticles
            });
`}</code></pre>
    <p>{`That looks like a bunch of code. Sort of. It’s spread out. The first part – `}<inlineCode parentName="p">{`CREATE_PARTICLES`}</inlineCode>{` – copies all current articles into a new array and adds `}<inlineCode parentName="p">{`action.N`}</inlineCode>{` new particles to the beginning. In my tests, this proved smoother than adding particles at the end. Each particle starts life at `}<inlineCode parentName="p">{`(action.x, action.y)`}</inlineCode>{` and gets a random movement vector. This is bad from Redux’s perspective. Reducers are supposed to be pure functions, and randomness is inherently impure. I think it’s fine in this case. The other possible approach would shove this logic into the action. That has benefits, but makes it harder to see all logic in one place. Anyway … The second part – `}<inlineCode parentName="p">{`TIME_TICK`}</inlineCode>{` – doesn’t copy the particles (although maybe it should). Arrays are passed by reference, so we’re mutating existing data either way. This is bad, but it’s not `}<em parentName="p">{`too`}</em>{` bad. It definitely works faster :) We filter out any particles that have left the visible area. For the rest, we add the movement vector to their position. Then we change the `}<inlineCode parentName="p">{`y`}</inlineCode>{` part of the vector using our `}<inlineCode parentName="p">{`Gravity`}</inlineCode>{` constant. That’s an easy way to implement acceleration. That’s it. Our reducer is done. Our particle generator works. Our thing animates smoothly. `}{`\\`}{`o/ `}<img parentName="p" {...{
        "src": "/c65374753f6c31d6507ef28498e7c685/blog-wp-content-uploads-2016-03-particles-step-5.gif",
        "alt": "Particle generator gif"
      }}></img></p>
    <h2 {...{
      "id": "important-discoveries"
    }}>{`Important Discoveries`}</h2>
    <p>{`Building this particle generator in React and Redux, I made three `}{`\\`}{`important discoveries:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Redux is much faster than I thought`}</strong>{`. You’d think creating a new copy of the state tree on each animation loop was crazy, but it works well. We’re probably creating only a shallow copy for the most part, which explains the speed.`}</li>
      <li parentName="ol"><strong parentName="li">{`Adding to JavaScript arrays is slow`}</strong>{`. Once we hit about 300 particles, adding new ones becomes visibly slow. Stop adding particles and you get smooth animation. This indicates that something about creating particles is slow: either adding to the array, or creating React component instances, or creating SVG DOM nodes.`}</li>
      <li parentName="ol"><strong parentName="li">{`SVG is also slow`}</strong>{`. To test the above hypothesis, I made the generator create 3000 particles on first click. The animation speed is `}<em parentName="li">{`terrible`}</em>{` at first and becomes okayish at around 1000 particles. This suggests that shallow copying big arrays and moving existing SVG nodes around is faster than adding new stuff. Here’s a gif:`}</li>
    </ol>
    <p>{`[`}{`caption id=`}{`"`}{`"`}{` align=`}{`"`}{`alignnone`}{`"`}{` width=`}{`"`}{`832`}{`"`}{`]`}<img parentName="p" {...{
        "src": "/baf79d08c1e7bf1f658b12cb5633326a/ug478Me.gif",
        "alt": null
      }}></img>{` 3000 particles is too many`}{`[`}{`/caption]`}</p>
    <h1 {...{
      "id": "fin"
    }}>{`Fin`}</h1>
    <p>{`There you go. Animations done with React, Redux, and d3. New superpower? Maybe. Here’s the recap:`}</p>
    <ul>
      <li parentName="ul">{`React handles rendering`}</li>
      <li parentName="ul">{`d3 calculates stuff`}</li>
      <li parentName="ul">{`Redux handles state`}</li>
      <li parentName="ul">{`element coordinates are state`}</li>
      <li parentName="ul">{`change coordinates on every `}<inlineCode parentName="li">{`requestAnimationFrame`}</inlineCode></li>
      <li parentName="ul">{`magic`}</li>
    </ul>
    <p>{`Don’t forget: this and other cool stuff will show up in the new version of my book, `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js/"
      }}>{`React+d3`}</a>{`, that’s launching this month. :) `}<strong parentName="p">{`Thanks`}</strong>{` to Primoz Cigler, Sigurt Bladt Dinesen, and Will Fanguy for reading drafts of this article`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      